import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { FormLabel } from './forms.component';
import { useDebouncedSearch } from '../../../lib/hooks/use-debounced-search';

export interface ISearchableOption {
  key: string;
  label: string;
}

interface ISearchableAutoCompleteProps<T extends ISearchableOption> {
  // Options for the current search term — the parent fetches them off onSearchChange.
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  // Fired with the debounced search term; the parent feeds it into its query.
  onSearchChange: (search: string) => void;
  loading?: boolean;
  label?: React.ReactNode;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}

// Standardized single-select autocomplete over a server-searched source: owns the
// debounce, surfaces the term via onSearchChange, keeps the current value selectable.
export const SearchableAutoComplete = <T extends ISearchableOption>({
  options,
  value,
  onChange,
  onSearchChange,
  loading = false,
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  placeholder,
  ariaLabel
}: ISearchableAutoCompleteProps<T>): React.ReactElement => {
  const { search, onSearchInput, resetSearch } = useDebouncedSearch();

  // Latest-ref so the debounced term reaches the parent whenever it changes,
  // without re-firing when the callback identity isn't stable.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  useEffect(() => { onSearchChangeRef.current(search); }, [search]);

  // The current selection joins the list so it stays a valid value even when a
  // search page or the source no longer includes it.
  const resolvedOptions = useMemo<T[]>(() => {
    if (value && !options.some((option) => option.key === value.key)) return [value, ...options];
    return options;
  }, [options, value]);

  const handleChange = useCallback((_event: React.ChangeEvent<{}>, option: T | null) => {
    resetSearch();
    onChange(option);
  }, [onChange, resetSearch]);

  const handleInputChange = useCallback((
    _event: React.ChangeEvent<{}>,
    next: string,
    reason: string
  ) => {
    // 'input' only — a selection/clear reset would clobber the live search text.
    if (reason === 'input') onSearchInput(next);
  }, [onSearchInput]);

  return (
    <div>
      {label && <FormLabel>{label}{required ? ' *' : ''}</FormLabel>}
      <Autocomplete<T>
        size="small"
        options={resolvedOptions}
        value={value}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, selected) => option.key === selected.key}
        onChange={handleChange}
        onInputChange={handleInputChange}
        // Server-searched: the default client filter would re-filter fresh results
        // against the stale debounced input.
        filterOptions={(unfiltered) => unfiltered}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            margin="dense"
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
          />
        )}
      />
    </div>
  );
};
